import { stopDraftResumePath } from '@folio/funnels/Stop/resume-step';
import { useStopDraft } from '@folio/funnels/Stop/useStopDraft';
import { StopDraftAlert } from '@folio/modules/PolicyWarningBanner/StopDraftAlert';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { PolicyTimeZoneProvider } from '@folio/utils/time-zone';
import {
  Alert,
  AlertTitle,
  Badge,
  KorintSuperLink,
  PageHeader,
  PolicyHeaderActions,
  PolicyLayout,
  PolicyStatus,
  useAssertedParams,
  useUserPermissions,
  ScheduledStopAlert,
} from '@korint/folio';
import { useSuspenseQuery } from '@tanstack/react-query';
import { customerControllerGetCustomerQueryOptions } from 'korint-sdk';
import { AlertCircleIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { TerminationButton } from './TerminationButton';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';

function Header({ stopFunnelBasePath }: { stopFunnelBasePath: string }) {
  const { can } = useUserPermissions({
    productId: 'garantiePlusAssistance',
    actions: ['STOP_POLICY'],
  });

  const { branchId: branchIdFromUrl } = useAssertedParams();
  const { t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();
  const { data: customer } = useSuspenseQuery({
    ...customerControllerGetCustomerQueryOptions(policy?.customers?.[0]!, {
      branchId: workingBranch?.id ?? branchIdFromUrl,
    }),
  });
  const productsConfig = usePolicyProductConfig('customerInformations');
  const title = customerDisplayName([customer], productsConfig.fieldsConfig) || customer.id;

  return (
    <PageHeader
      className="mb-0"
      title={`Contrat ${title} ${policy.externalId ?? ''}`}
      subtitle={
        <div className="flex flex-row gap-2">
          <KorintSuperLink subpath={`/policies/${policy.id}`} />
          <Badge variant="outline">{t(`products.${policy.productId}.label`)}</Badge>
          <PolicyStatus policy={policy} hasSigned={!workingBranch} />
        </div>
      }
      right={
        <PolicyHeaderActions
          gestures={
            <>{can.STOP_POLICY && <TerminationButton funnelBasePath={stopFunnelBasePath} />}</>
          }
        />
      }
    />
  );
}

function WarningBanners({ stopFunnelBasePath }: { stopFunnelBasePath: string }) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();

  return (
    <>
      <ScheduledStopAlert />
      <StopDraftAlert funnelBasePath={stopFunnelBasePath} />
      {policy.status === 'POLICY_STOPPED' && (
        <Alert className="text-destructive">
          <AlertTitle className="flex items-center gap-4 pt-1">
            <AlertCircleIcon />
            {t('warningBanner.policyStopped', {
              reason: policy.stop?.reason
                ? t([
                    `${policy.productId}.stopReasons.${policy.stop.reason}`,
                    `stopReasons.${policy.stop.reason}`,
                  ])
                : '',
            })}
          </AlertTitle>
        </Alert>
      )}
    </>
  );
}

export function BrokerDashboardPolicyLayout({
  stopFunnelBasePath,
}: {
  stopFunnelBasePath: string;
}) {
  const { policyId, branchId } = useAssertedParams();
  const basePath = `/policies/${policyId}${branchId ? `/${branchId}` : ''}`;

  const { policyOnMain, workingBranch } = usePolicyOnDashboard();
  const isStopBranch = workingBranch?.branchType === 'STOP';
  const { draft, isLoading: isDraftLoading } = useStopDraft(policyId, { enabled: isStopBranch });

  const tabs = [
    {
      label: 'Tableau de bord',
      href: basePath,
    },
    {
      label: 'Informations assuré',
      href: `${basePath}/customer`,
    },
    {
      label: 'Documents',
      href: `${basePath}/documents`,
    },
    {
      label: 'Factures',
      href: `${basePath}/invoices`,
    },
    {
      label: 'Événements',
      href: `${basePath}/events`,
    },
  ];

  // A STOP branch has no tab content until it carries a proof to review.
  if (isStopBranch && !policyOnMain.isStopAwaitingValidation) {
    // Navigate fires once: an unresolved draft reads no stopDate. Same gate as StopFunnelGuard.
    if (isDraftLoading) {
      return null;
    }

    return (
      <Navigate
        replace
        to={
          policyOnMain.stop
            ? `${stopFunnelBasePath}/${policyId}`
            : stopDraftResumePath({ funnelBasePath: stopFunnelBasePath, policyId, draft })
        }
      />
    );
  }

  return (
    <PolicyTimeZoneProvider>
      <PolicyLayout
        header={
          <>
            <Header stopFunnelBasePath={stopFunnelBasePath} />
            <WarningBanners stopFunnelBasePath={stopFunnelBasePath} />
          </>
        }
        tabs={tabs}
      />
    </PolicyTimeZoneProvider>
  );
}
